import Heading from '@/common/Heading';
import Badge from '@/common/Badge';
import Button, { ButtonType } from '@/components/Button';
import Text from '@/components/Text';
import withAuth from '@/components/withAuth';
import useCurrentUser from '@/hooks/useCurrentUser';
import MainLayout from '@/layouts/MainLayout';
import {
  ParentAccountStatus,
  ParentAccountStatusToText,
} from '@/lib/Account/types';
import AuthService from '@/lib/Auth';
import Analytics from '@/system/Analytics';
import { OnboardingEvents } from '@/system/Analytics/events/OnboardingEvents';
import { useQueryClient } from '@tanstack/react-query';
import {
  Icon16Chevron,
  Icon24DocumentOutline,
  Icon24LogoInstagram,
  Icon24LogoTwitter,
  Icon24MoneySendOutline,
  Icon24QuestionOutline,
  Icon24UserOutline,
} from '@vkontakte/icons';
import clsx from 'clsx';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';
import { NextPageWithLayout } from '../_app.page';
import ParentModal from './ParentModal';
import ListItem from '@/common/ListItem';
import StudentProfile from './components/StudentProfile';
import PhoneUtils from '@/utils/PhoneUtils';
import { twMerge } from 'tailwind-merge';

interface SettingItem {
  title: string;
  icon: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  href?: string;
  badge?: React.ReactNode;
  externalHref?: boolean;
  enabled?: boolean;
}

interface SocialItem {
  icon: React.ReactNode;
  href: string;
}

const SOCIALS: SocialItem[] = [
  {
    icon: <Icon24LogoInstagram />,
    href: 'https://instagram.com/joinbloomapp',
  },
  {
    icon: <Image src="/images/logo_tiktok_28.svg" alt="Tiktok logo" height={24} width={24} />,
    href: 'https://www.tiktok.com/@joinbloom',
  },
  {
    icon: <Icon24LogoTwitter />,
    href: 'https://twitter.com/joinbloomapp',
  },
];


function Profile() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [showParentModal, setShowParentModal] = useState<boolean>(false);
  const { data: user, isLoading, refetch } = useCurrentUser();

  const settings: SettingItem[] = useMemo(
    () => [
      {
        title: 'My parent',
        badge: (
          <Badge variant='default' className={twMerge("uppercase h-6", user?.parent?.status === ParentAccountStatus.APPROVED ? 'text-success bg-success/10' : 'text-primary bg-primary/10')}
          >
            <span className="text-[12px]">
              {ParentAccountStatusToText[user?.parent?.status as string]}
            </span>
          </Badge>
          ),
        icon: <Icon24UserOutline />,
        onClick: (e: React.MouseEvent<HTMLButtonElement>) => setShowParentModal(true),
        enabled: user?.parent?.status == ParentAccountStatus.APPROVED,
      },
      {
        title: 'Transfers',
        icon: <Icon24MoneySendOutline />,
        href: '/transfers',
        externalHref: false,
        enabled: true,
      },
      {
        title: 'Documents',
        icon: <Icon24DocumentOutline />,
        href: '/documents',
        externalHref: false,
        enabled: true,
      },
      {
        title: 'FAQ',
        icon: <Icon24QuestionOutline />,
        href: 'https://intercom.help/bloomapp/en/',
        externalHref: true,
        enabled: true,
      },
    ],
    [user?.parent?.status]
  );

  const renderListItem = (item: SettingItem, index: number) => {
    const body = (
      <div className="flex items-center space-x-4 w-full">
        <div className="text-icon-primary">{item.icon}</div>
        <div className="flex flex-row justify-between w-full">
          <div className="w-3/4 flex space-x-3 items-center">
            <h4 className="truncate">{item.title}</h4>
            {item.badge}
          </div>
        </div>
        <div className="flex justify-center items-center text-secondary">
          <Icon16Chevron />
        </div>
      </div>
    );

    const externalLinkProps = item.externalHref
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : {};

    if (!item.enabled) {
      return null;
    }

    return (
      <ListItem
        key={item.title}
        href={item.href}
        target={item.externalHref ? '_blank' : '_self'}
        className={clsx({ 'cursor-default': !item.href && !item.onClick })}
      >
        {body}
      </ListItem>
      // <li key={item.title}>
      //   {item.href ? (
      //     <Link
      //       href={item.href}
      //       className="flex w-full justify-between items-center px-6 py-3 text-left hover:bg-darkglass"
      //       {...externalLinkProps}
      //     >
      //       {body}
      //     </Link>
      //   ) : (
      //     <button
      //       onClick={item.onClick}
      //       className={
      //         'flex w-full justify-between items-center px-6 py-3 text-left hover:bg-darkglass'
      //       }
      //     >
      //       {body}
      //     </button>
      //   )}
      // </li>
    );
  };

  const renderSocial = (item: SocialItem, index: number) => {
    return (
      <Link
        key={index}
        className="w-11 h-11 rounded-full bg-darkglass flex justify-center items-center text-icon-primary hover:bg-darkglass"
        href={item.href}
        rel="noopener noreferrer"
        target="_blank"
      >
        {item.icon}
      </Link>
    );
  };

  if (isLoading || !user?.account) {
    return (
      <div className="max-w-screen-lg mx-auto md:py-6">
        <Head>
          <title>Profile | Bloom</title>
        </Head>
        <Heading as="h1">Profile</Heading>
        <div className="rounded-xl bg-darkglass p-6 text-secondary">
          {isLoading ? 'Loading profile...' : 'Profile not available'}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-screen-lg mx-auto md:py-6">
      <Head>
        <title>Profile | Bloom</title>
      </Head>

      <Heading as="h1">Profile</Heading>

      <div className="flex flex-col gap-4">
        <div className="rounded-xl bg-darkglass p-6">
          <h2>{user?.account?.name || 'N/A'}</h2>
          <h4 className="text-secondary">
            {user?.account?.phoneNumber
              ? PhoneUtils.formatInternational(user.account.phoneNumber)
              : 'N/A'}
          </h4>
        </div>
        <div className="rounded-xl bg-darkglass overflow-hidden">
          {settings.map(renderListItem)}
        </div>

        <div className="flex flex-col bg-darkglass rounded-xl overflow-hidden">
          {user?.account?.studentStatus && (            
              <StudentProfile user={user} onStudentProfileRefetch={() => refetch()}/>
          )}
        </div>
      </div>
      <div className="flex flex-col items-center mt-8">
        <ul className="flex space-x-5">{SOCIALS.map(renderSocial)}</ul>
        <Button
          type={ButtonType.Link}
          className="text-u-negative font-medium mt-6"
          onClick={() => {
            Analytics.track(OnboardingEvents.CLICKED_LOGOUT);
            queryClient.clear();
            AuthService.logout();
            router.replace('/login');
          }}
        >
          Log out
        </Button>
        <h6 className="text-secondary mt-3">v1.01</h6>
      </div>
      <ParentModal open={showParentModal} closeModal={() => setShowParentModal(false)} />
    </div>
  );
}

const ProfileWithAuth = withAuth(Profile) as NextPageWithLayout;

export default ProfileWithAuth;

ProfileWithAuth.getLayout = MainLayout;
